import { readFile, writeFile } from "node:fs/promises";
import path from "node:path";

import { parsePrimeUiComponentExportManifest } from "../lib/api-v1-contract.js";
import { ensureDir } from "../lib/fs.js";
import { buildImportGraph } from "../lib/import-graph.js";
import { buildUnifiedDiff } from "../lib/text-diff.js";
import {
  applyDependencyRequirementsToPackageJson,
  createCopyId,
  ensureSafeTargetPath,
  fileExists,
  isBinaryBuffer,
  readJsonFile,
  resolveManifestCandidateFiles,
  resolveSingleExportDirectory,
  toProjectRelative,
  toPosixPath,
} from "./copy-service-shared.js";
import {
  buildConflictReportArtifact,
  buildExportManifestArtifact,
  buildReferenceExportArtifact,
} from "./runtime-guidance.js";
import type {
  PrimeUiComponentExportManifest,
  PrimeUiComponentExportSelectedBlock,
  PrimeUiConflictFile,
  PrimeUiCopyConflictReportEntry,
  PrimeUiCopyRegistryComponentInsertion,
  PrimeUiCopyRegistryComponentConflictReport,
  PrimeUiCopyRegistryComponentResult,
  PrimeUiSkippedCopyFile,
} from "../types.js";

type CopyRegistryComponentServiceInput = {
  projectRoot: string;
  exportsRoot: string;
  componentId: string;
};

type RegistryComponentFileTransfer = {
  sourcePath: string;
  targetPath: string;
};

const PACKAGE_JSON_RELATIVE_PATH = "package.json";
const VIRTUAL_ROUTE_SEGMENT = "__primeui-component-export";
const COMPONENT_PAGES_PREFIX = "src/components/pages/";
const COMPONENT_UI_PREFIX = "src/components/ui/";
const COMPONENT_SHARED_PREFIX = "src/components/shared/";
const NATURAL_SUPPORT_PREFIXES = [
  COMPONENT_UI_PREFIX,
  "src/lib/",
  "src/types/",
  "src/contexts/",
];
const PUBLIC_PREFIX = "public/";
const PUBLIC_ASSET_REFERENCE_RE =
  /["'`(]\/([^"'`()\s]+\.(?:avif|gif|ico|jpeg|jpg|json|mp4|otf|png|svg|ttf|txt|webm|webmanifest|webp|woff|woff2|xml))(?:\?[^"'`()\s]*)?["'`)]/giu;

function normalizeComponentId(componentId: string): string {
  const trimmed = componentId.trim();
  if (!trimmed) {
    throw new Error("componentId must not be empty.");
  }

  return trimmed;
}

function normalizeManifestFilePath(filePath: string): string {
  const trimmed = filePath.trim();
  if (!trimmed) {
    throw new Error("Component export manifest contains an empty file path.");
  }

  return toPosixPath(trimmed).replace(/^\.\/+/, "");
}

function uniqueNormalizedManifestFiles(files: string[]): string[] {
  return [...new Set(files.map(normalizeManifestFilePath))];
}

function getSuccessfulExportableFiles(
  manifest: PrimeUiComponentExportManifest,
): string[] {
  return manifest.export.exportables.flatMap((exportable) =>
    exportable.isReadyToExport && exportable.manifest.success
      ? exportable.manifest.files
      : [],
  );
}

function getComponentCopyManifestFiles(
  manifest: PrimeUiComponentExportManifest,
): string[] {
  return uniqueNormalizedManifestFiles([
    ...manifest.page.manifest.files,
    ...getSuccessfulExportableFiles(manifest),
  ]);
}

function isVirtualRouteFile(relativePath: string): boolean {
  const segments = relativePath.split("/");

  return (
    segments[0] === "src" &&
    segments[1] === "app" &&
    segments.includes(VIRTUAL_ROUTE_SEGMENT)
  );
}

function isVirtualComponentExportPageFile(relativePath: string): boolean {
  const segments = relativePath.split("/");
  const virtualRouteIndex = segments.indexOf(VIRTUAL_ROUTE_SEGMENT);

  return (
    isVirtualRouteFile(relativePath) &&
    virtualRouteIndex >= 0 &&
    segments.length === virtualRouteIndex + 3 &&
    segments[virtualRouteIndex + 2] === "page.tsx"
  );
}

function getVirtualComponentRelativePath(
  relativePath: string,
  componentId: string,
): string | null {
  const prefix = `${COMPONENT_PAGES_PREFIX}${VIRTUAL_ROUTE_SEGMENT}/${componentId}/`;
  if (!relativePath.startsWith(prefix)) {
    return null;
  }

  const componentRelativePath = relativePath.slice(prefix.length);
  return componentRelativePath || null;
}

function getComponentPageSourceArea(relativePath: string): string | null {
  if (!relativePath.startsWith(COMPONENT_PAGES_PREFIX)) {
    return null;
  }

  const rest = relativePath.slice(COMPONENT_PAGES_PREFIX.length);
  const slashIndex = rest.indexOf("/");
  if (slashIndex <= 0) {
    return null;
  }

  const sourceArea = rest.slice(0, slashIndex);
  return sourceArea === VIRTUAL_ROUTE_SEGMENT ? null : sourceArea;
}

function getSourceAreaComponentRelativePath(
  relativePath: string,
  sourceAreas: Set<string>,
): string | null {
  const sourceArea = getComponentPageSourceArea(relativePath);
  if (!sourceArea || !sourceAreas.has(sourceArea)) {
    return null;
  }

  const prefix = `${COMPONENT_PAGES_PREFIX}${sourceArea}/`;
  const componentRelativePath = relativePath.slice(prefix.length);
  return componentRelativePath || null;
}

function isComponentEntryFile(
  relativePath: string,
  componentId: string,
): boolean {
  if (!relativePath.startsWith(COMPONENT_PAGES_PREFIX)) {
    return false;
  }

  const extension = path.extname(relativePath);
  if (!extension) {
    return false;
  }

  const basename = path.basename(relativePath, extension);
  if (basename === componentId) {
    return true;
  }

  return (
    basename === "index" &&
    path.basename(path.dirname(relativePath)) === componentId
  );
}

function isManifestSupportFile(relativePath: string): boolean {
  return NATURAL_SUPPORT_PREFIXES.some((prefix) =>
    relativePath.startsWith(prefix),
  );
}

function isEnvFile(relativePath: string): boolean {
  return relativePath === ".env" || relativePath.startsWith(".env.");
}

function isSearchRuntimeFile(relativePath: string): boolean {
  return (
    relativePath.startsWith("src/app/api/search/") ||
    relativePath.startsWith("src/lib/search/") ||
    relativePath.startsWith("src/lib/search-runtime/") ||
    relativePath.startsWith("src/search/")
  );
}

function isProjectLevelFile(relativePath: string): boolean {
  return (
    isEnvFile(relativePath) ||
    isSearchRuntimeFile(relativePath) ||
    relativePath.startsWith("scripts/") ||
    relativePath.startsWith("src/app/") ||
    relativePath.startsWith("src/components/header/") ||
    relativePath.startsWith("src/components/footer/") ||
    relativePath.startsWith("src/components/layout/")
  );
}

function toComponentSharedTargetPath(componentRelativePath: string): string {
  return `${COMPONENT_SHARED_PREFIX}${componentRelativePath}`;
}

function toImportPath(relativePath: string): string {
  const withoutExtension = relativePath.replace(/\.[cm]?[jt]sx?$/u, "");
  const withoutIndex = withoutExtension.replace(/\/index$/u, "");
  return `@/${withoutIndex.replace(/^src\//u, "")}`;
}

function toPascalComponentName(componentId: string): string {
  return componentId
    .split(/[^a-zA-Z0-9]+/u)
    .filter(Boolean)
    .map((part) => `${part.charAt(0).toUpperCase()}${part.slice(1)}`)
    .join("");
}

function toVariableName(componentName: string): string {
  if (!componentName) {
    return "componentProps";
  }

  return `${componentName.charAt(0).toLowerCase()}${componentName.slice(1)}Props`;
}

function toSafeCamelIdentifier(input: string, fallback: string): string {
  const parts = input
    .trim()
    .split(/[^a-zA-Z0-9]+/u)
    .filter(Boolean);

  if (parts.length === 0) {
    return fallback;
  }

  const pascalName = parts
    .map((part) => {
      const normalizedPart = part.toLowerCase();
      return `${normalizedPart.charAt(0).toUpperCase()}${normalizedPart.slice(1)}`;
    })
    .join("");

  if (!pascalName) {
    return fallback;
  }

  if (/^[0-9]/u.test(pascalName)) {
    return `block${pascalName}`;
  }

  return `${pascalName.charAt(0).toLowerCase()}${pascalName.slice(1)}`;
}

function isDefaultComponentExportBlock(
  selectedBlock: PrimeUiComponentExportSelectedBlock,
): boolean {
  return (
    selectedBlock.blockId ===
    `component-export:${selectedBlock.componentId}:block`
  );
}

function toPropsVariableName(input: {
  componentName: string;
  selectedBlock: PrimeUiComponentExportSelectedBlock;
}): string {
  if (isDefaultComponentExportBlock(input.selectedBlock)) {
    return toVariableName(input.componentName);
  }

  return `${toSafeCamelIdentifier(
    input.selectedBlock.blockId,
    input.componentName
      ? toVariableName(input.componentName).replace(/Props$/u, "")
      : "component",
  )}Props`;
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/gu, "\\$&");
}

function sourceImportSpecifiers(relativePath: string): string[] {
  const withAlias = toImportPath(relativePath);
  const specifiers = [withAlias];
  if (withAlias.endsWith("/index")) {
    specifiers.push(withAlias.replace(/\/index$/u, ""));
  }

  return [...new Set(specifiers)];
}

function rewriteComponentLocalImports(
  content: string,
  transfers: RegistryComponentFileTransfer[],
): string {
  let updated = content;
  for (const transfer of transfers) {
    const targetSpecifier = toImportPath(transfer.targetPath);
    for (const sourceSpecifier of sourceImportSpecifiers(transfer.sourcePath)) {
      updated = updated.replace(
        new RegExp(escapeRegExp(sourceSpecifier), "gu"),
        targetSpecifier,
      );
    }
  }

  return updated;
}

function collectReferencedPublicAssetFiles(content: string): string[] {
  const referencedFiles = new Set<string>();
  let match: RegExpExecArray | null = null;
  PUBLIC_ASSET_REFERENCE_RE.lastIndex = 0;
  while ((match = PUBLIC_ASSET_REFERENCE_RE.exec(content)) !== null) {
    const referencedPath = match[1]?.trim();
    if (referencedPath) {
      referencedFiles.add(
        `${PUBLIC_PREFIX}${normalizeManifestFilePath(referencedPath)}`,
      );
    }
  }

  return [...referencedFiles];
}

function findComponentSourceAreas(relativePaths: string[]): Set<string> {
  const sourceAreas = new Set<string>();
  for (const relativePath of relativePaths) {
    const sourceArea = getComponentPageSourceArea(relativePath);
    if (sourceArea) {
      sourceAreas.add(sourceArea);
    }
  }

  return sourceAreas;
}

function getComponentLocalTargetPath(input: {
  relativePath: string;
  componentId: string;
  sourceAreas: Set<string>;
}): string | null {
  const virtualRelativePath = getVirtualComponentRelativePath(
    input.relativePath,
    input.componentId,
  );
  if (virtualRelativePath) {
    return toComponentSharedTargetPath(virtualRelativePath);
  }

  const sourceAreaRelativePath = getSourceAreaComponentRelativePath(
    input.relativePath,
    input.sourceAreas,
  );
  if (sourceAreaRelativePath) {
    return toComponentSharedTargetPath(sourceAreaRelativePath);
  }

  return null;
}

function getTransferTargetPath(input: {
  relativePath: string;
  componentId: string;
  sourceAreas: Set<string>;
  referencedPublicFiles: Set<string>;
}): string | null {
  if (
    input.relativePath === PACKAGE_JSON_RELATIVE_PATH ||
    isVirtualRouteFile(input.relativePath) ||
    isProjectLevelFile(input.relativePath)
  ) {
    return null;
  }

  const componentLocalTargetPath = getComponentLocalTargetPath(input);
  if (componentLocalTargetPath) {
    return componentLocalTargetPath;
  }

  if (isManifestSupportFile(input.relativePath)) {
    return input.relativePath;
  }

  if (
    input.relativePath.startsWith(PUBLIC_PREFIX) &&
    input.referencedPublicFiles.has(input.relativePath)
  ) {
    return input.relativePath;
  }

  return null;
}

async function collectReferencedPublicFiles(input: {
  exportPath: string;
  graphInternalFiles: Set<string>;
  manifestFileSet: Set<string>;
}): Promise<Set<string>> {
  const referencedPublicFiles = new Set<string>();
  for (const relativePath of input.graphInternalFiles) {
    if (!input.manifestFileSet.has(relativePath)) {
      continue;
    }

    const sourcePath = path.join(input.exportPath, relativePath);
    let content = "";
    try {
      content = await readFile(sourcePath, "utf-8");
    } catch {
      continue;
    }

    for (const referencedFile of collectReferencedPublicAssetFiles(content)) {
      if (input.manifestFileSet.has(referencedFile)) {
        referencedPublicFiles.add(referencedFile);
      }
    }
  }

  return referencedPublicFiles;
}

function buildFileTransfers(input: {
  files: string[];
  componentId: string;
  graphInternalFiles: Set<string>;
  referencedPublicFiles: Set<string>;
}): RegistryComponentFileTransfer[] {
  const sourceAreas = findComponentSourceAreas([...input.graphInternalFiles]);
  const transfers: RegistryComponentFileTransfer[] = [];
  const usedTargetPaths = new Set<string>();

  for (const relativePath of input.files) {
    const isGraphFile = input.graphInternalFiles.has(relativePath);
    const isReferencedPublicFile =
      input.referencedPublicFiles.has(relativePath);
    if (!isGraphFile && !isReferencedPublicFile) {
      continue;
    }

    const targetPath = getTransferTargetPath({
      relativePath,
      componentId: input.componentId,
      sourceAreas,
      referencedPublicFiles: input.referencedPublicFiles,
    });
    if (!targetPath || usedTargetPaths.has(targetPath)) {
      continue;
    }

    usedTargetPaths.add(targetPath);
    transfers.push({
      sourcePath: relativePath,
      targetPath,
    });
  }

  return transfers.sort((a, b) => a.targetPath.localeCompare(b.targetPath));
}

function toSkippedFile(
  targetPath: string,
  reason: PrimeUiSkippedCopyFile["reason"],
): PrimeUiSkippedCopyFile {
  return {
    targetPath,
    reason,
  };
}

function splitManifestFiles(input: {
  files: string[];
  componentId: string;
  copyableFiles: string[];
}): {
  manifestFiles: string[];
  componentEntryFiles: string[];
  skippedFiles: PrimeUiSkippedCopyFile[];
} {
  const manifestFiles = input.files.map(normalizeManifestFilePath);
  const copyableFileSet = new Set(input.copyableFiles);
  const componentEntryFiles: string[] = [];
  const skippedFiles: PrimeUiSkippedCopyFile[] = [];

  for (const relativePath of manifestFiles) {
    if (relativePath === PACKAGE_JSON_RELATIVE_PATH) {
      skippedFiles.push(
        toSkippedFile(relativePath, "package_json_managed_as_dependencies"),
      );
      continue;
    }

    if (isVirtualRouteFile(relativePath)) {
      skippedFiles.push(toSkippedFile(relativePath, "virtual_component_route"));
      continue;
    }

    if (isComponentEntryFile(relativePath, input.componentId)) {
      componentEntryFiles.push(relativePath);
    }

    if (!copyableFileSet.has(relativePath)) {
      skippedFiles.push(
        toSkippedFile(relativePath, "not_component_dependency"),
      );
    }
  }

  return {
    manifestFiles,
    componentEntryFiles,
    skippedFiles,
  };
}

async function createConflictReportEntry(input: {
  sourceFilePath: string;
  targetFilePath: string;
  exportPath: string;
  projectRoot: string;
  sourceBuffer?: Buffer;
}): Promise<PrimeUiCopyConflictReportEntry | null> {
  const sourceBuffer =
    input.sourceBuffer ?? (await readFile(input.sourceFilePath));
  let targetBuffer: Buffer | null = null;
  try {
    targetBuffer = await readFile(input.targetFilePath);
  } catch {
    targetBuffer = null;
  }

  if (!targetBuffer || sourceBuffer.equals(targetBuffer)) {
    return null;
  }

  const sourceRelative = toProjectRelative(
    input.exportPath,
    input.sourceFilePath,
  );
  const targetRelative = toProjectRelative(
    input.projectRoot,
    input.targetFilePath,
  );
  const isBinary = isBinaryBuffer(sourceBuffer) || isBinaryBuffer(targetBuffer);

  return {
    sourcePath: sourceRelative,
    targetPath: targetRelative,
    diff: isBinary
      ? "Binary files differ."
      : buildUnifiedDiff({
          oldText: targetBuffer.toString("utf-8"),
          newText: sourceBuffer.toString("utf-8"),
          oldLabel: `user/${targetRelative}`,
          newLabel: `export/${sourceRelative}`,
        }),
    isBinary,
  };
}

function buildMessage(input: {
  componentId: string;
  componentImportPath: string;
  status: PrimeUiCopyRegistryComponentResult["status"];
  reportPath: string;
  newFiles: string[];
  identicalFiles: string[];
  conflictFiles: PrimeUiConflictFile[];
  skippedFiles: PrimeUiSkippedCopyFile[];
  addedDependenciesCount: number;
  dependencyConflictCount: number;
  missingManifestFiles: string[];
}): string {
  const parts = [
    input.status === "completed"
      ? `Registry component "${input.componentId}" copied from the component manifest.`
      : `Registry component "${input.componentId}" needs review before transfer is complete.`,
    `Report: ${input.reportPath}.`,
  ];

  if (input.newFiles.length > 0) {
    parts.push(`${input.newFiles.length} new file(s) copied.`);
  }
  if (input.identicalFiles.length > 0) {
    parts.push(`${input.identicalFiles.length} file(s) already identical.`);
  }
  if (input.conflictFiles.length > 0) {
    parts.push(
      `${input.conflictFiles.length} conflicting file(s) were not overwritten.`,
    );
  }
  if (input.skippedFiles.length > 0) {
    parts.push(
      `${input.skippedFiles.length} manifest file(s) skipped, including virtual route, package metadata, or non-component context entries.`,
    );
  }
  if (input.missingManifestFiles.length > 0) {
    parts.push(
      `Missing manifest file(s): ${input.missingManifestFiles.join(", ")}.`,
    );
  }
  if (input.addedDependenciesCount > 0) {
    parts.push(
      "Missing dependencies were added to package.json but are NOT installed.",
    );
  }
  if (input.dependencyConflictCount > 0) {
    parts.push(
      "Dependency version conflicts require manual resolution before completion.",
    );
  }

  parts.push(
    `The virtual component export page was skipped; manually import ${input.componentImportPath} into a real target page.`,
  );

  return parts.join(" ");
}

function getDefaultSelectedBlock(
  manifest: PrimeUiComponentExportManifest,
): PrimeUiComponentExportSelectedBlock {
  const componentId = manifest.component.componentId;

  return {
    blockId: `component-export:${componentId}:block`,
    componentId,
    contentKey: componentId,
    occurrenceIndex: 1,
    props: null,
    slot: null,
  };
}

function getSelectedBlock(
  manifest: PrimeUiComponentExportManifest,
): PrimeUiComponentExportSelectedBlock {
  return manifest.block ?? getDefaultSelectedBlock(manifest);
}

function getVirtualRouteFile(manifestFiles: string[]): string | null {
  return (
    manifestFiles.find(isVirtualComponentExportPageFile) ??
    manifestFiles.find(isVirtualRouteFile) ??
    null
  );
}

function readBalancedObjectLiteral(
  input: string,
  startIndex: number,
): string | null {
  if (input[startIndex] !== "{") {
    return null;
  }

  let depth = 0;
  let quote: '"' | "'" | "`" | null = null;
  let escaped = false;

  for (let index = startIndex; index < input.length; index += 1) {
    const char = input[index];

    if (escaped) {
      escaped = false;
      continue;
    }

    if (quote) {
      if (char === "\\") {
        escaped = true;
        continue;
      }
      if (char === quote) {
        quote = null;
      }
      continue;
    }

    if (char === '"' || char === "'" || char === "`") {
      quote = char;
      continue;
    }

    if (char === "{") {
      depth += 1;
      continue;
    }

    if (char !== "}") {
      continue;
    }

    depth -= 1;
    if (depth === 0) {
      return input.slice(startIndex, index + 1);
    }
  }

  return null;
}

function extractContentDataPropsCode(input: {
  source: string;
  contentKey: string;
}): string | null {
  const contentDataIndex = input.source.indexOf("const contentData");
  if (contentDataIndex < 0) {
    return null;
  }

  const keyPattern = new RegExp(
    `["']${escapeRegExp(input.contentKey)}["']\\s*:`,
    "u",
  );
  const keyMatch = keyPattern.exec(input.source.slice(contentDataIndex));
  if (!keyMatch) {
    return null;
  }

  const valueStartSearchIndex =
    contentDataIndex + keyMatch.index + keyMatch[0].length;
  const objectStartIndex = input.source.indexOf("{", valueStartSearchIndex);
  if (objectStartIndex < 0) {
    return null;
  }

  return readBalancedObjectLiteral(input.source, objectStartIndex);
}

async function buildRegistryComponentInsertion(input: {
  componentImportPath: string;
  exportPath: string;
  manifestFiles: string[];
  selectedBlock: PrimeUiComponentExportSelectedBlock;
}): Promise<PrimeUiCopyRegistryComponentInsertion> {
  const componentName = toPascalComponentName(input.selectedBlock.componentId);
  const propsVariableName = toPropsVariableName({
    componentName,
    selectedBlock: input.selectedBlock,
  });
  const virtualRouteFile = getVirtualRouteFile(input.manifestFiles);
  const referenceFiles = virtualRouteFile ? [virtualRouteFile] : [];
  let materializedPropsCode = input.selectedBlock.props
    ? JSON.stringify(input.selectedBlock.props, null, 2)
    : "{}";

  if (virtualRouteFile) {
    try {
      const virtualRouteSource = await readFile(
        path.join(input.exportPath, virtualRouteFile),
        "utf-8",
      );
      materializedPropsCode =
        extractContentDataPropsCode({
          source: virtualRouteSource,
          contentKey: input.selectedBlock.contentKey,
        }) ?? materializedPropsCode;
    } catch {
      // Older component exports may not contain a readable virtual route yet.
    }
  }

  return {
    imports: [`import ${componentName} from "${input.componentImportPath}";`],
    propsCode: `const ${propsVariableName} = ${materializedPropsCode};`,
    jsx: `<${componentName} {...${propsVariableName}} />`,
    referenceFiles,
  };
}

export async function copyRegistryComponentFromExport(
  input: CopyRegistryComponentServiceInput,
): Promise<PrimeUiCopyRegistryComponentResult> {
  const normalizedComponentId = normalizeComponentId(input.componentId);
  const { exportId, exportPath } = await resolveSingleExportDirectory(
    input.exportsRoot,
  );
  const manifestPath = path.join(
    input.exportsRoot,
    `${exportId}.manifest.json`,
  );

  if (!(await fileExists(manifestPath))) {
    throw new Error(
      `Component export manifest is missing at ${manifestPath}. Run create_component_export and download_component_export to regenerate it.`,
    );
  }

  const manifest = parsePrimeUiComponentExportManifest(
    await readJsonFile<unknown>(manifestPath),
  );
  if (manifest.export.id !== exportId || manifest.exportId !== exportId) {
    throw new Error(
      `Component export manifest mismatch for id "${exportId}". Run create_component_export and download_component_export again.`,
    );
  }
  if (manifest.component.componentId !== normalizedComponentId) {
    throw new Error(
      `Component export manifest mismatch: expected componentId "${normalizedComponentId}", got "${manifest.component.componentId}".`,
    );
  }

  const initialSelection = splitManifestFiles({
    files: getComponentCopyManifestFiles(manifest),
    componentId: normalizedComponentId,
    copyableFiles: [],
  });
  const manifestFileSet = new Set(initialSelection.manifestFiles);
  const componentEntryCandidates = await resolveManifestCandidateFiles({
    exportPath,
    manifestOwnerLabel: `registry component "${normalizedComponentId}"`,
    files: initialSelection.componentEntryFiles,
    requireExisting: false,
  });

  const newFiles: string[] = [];
  const identicalFiles: string[] = [];
  const conflictFiles: PrimeUiConflictFile[] = [];
  const conflictReportEntries: PrimeUiCopyConflictReportEntry[] = [];
  const missingManifestFiles = new Set<string>();
  const existingComponentEntryFiles: string[] = [];

  for (const sourceFilePath of componentEntryCandidates) {
    const relativeToExport = toProjectRelative(exportPath, sourceFilePath);
    if (await fileExists(sourceFilePath)) {
      existingComponentEntryFiles.push(sourceFilePath);
      continue;
    }
    missingManifestFiles.add(relativeToExport);
  }

  const dependencyGraph =
    existingComponentEntryFiles.length > 0
      ? await buildImportGraph({
          projectRoot: exportPath,
          entryFiles: existingComponentEntryFiles,
          followInternalImports: true,
        })
      : { internalFiles: [], externalPackages: [] };
  const graphInternalFiles = new Set(
    dependencyGraph.internalFiles.map((filePath) =>
      toProjectRelative(exportPath, filePath),
    ),
  );
  const referencedPublicFiles = await collectReferencedPublicFiles({
    exportPath,
    graphInternalFiles,
    manifestFileSet,
  });
  const fileTransfers = buildFileTransfers({
    files: initialSelection.manifestFiles,
    componentId: normalizedComponentId,
    graphInternalFiles,
    referencedPublicFiles,
  });
  const copyableSourceFiles = fileTransfers.map(
    (transfer) => transfer.sourcePath,
  );
  const copyableFiles = fileTransfers.map((transfer) => transfer.targetPath);
  const transferBySourcePath = new Map(
    fileTransfers.map((transfer) => [transfer.sourcePath, transfer]),
  );
  const componentEntryTransfer = fileTransfers.find((transfer) =>
    initialSelection.componentEntryFiles.includes(transfer.sourcePath),
  );
  const componentImportPath = toImportPath(
    componentEntryTransfer?.targetPath ??
      `${COMPONENT_SHARED_PREFIX}${normalizedComponentId}.tsx`,
  );
  const { manifestFiles, skippedFiles } = splitManifestFiles({
    files: initialSelection.manifestFiles,
    componentId: normalizedComponentId,
    copyableFiles: copyableSourceFiles,
  });
  const selectedBlock = getSelectedBlock(manifest);
  const insertion = await buildRegistryComponentInsertion({
    componentImportPath,
    exportPath,
    manifestFiles,
    selectedBlock,
  });
  const candidateFiles = await resolveManifestCandidateFiles({
    exportPath,
    manifestOwnerLabel: `registry component "${normalizedComponentId}"`,
    files: copyableSourceFiles,
    requireExisting: false,
  });

  for (const sourceFilePath of candidateFiles) {
    const relativeToExport = toProjectRelative(exportPath, sourceFilePath);
    const sourceExists = await fileExists(sourceFilePath);
    if (!sourceExists) {
      missingManifestFiles.add(relativeToExport);
      continue;
    }
    const transfer = transferBySourcePath.get(relativeToExport);
    if (!transfer) {
      continue;
    }

    const targetRelativePath = transfer.targetPath;
    const targetFilePath = path.join(input.projectRoot, targetRelativePath);
    ensureSafeTargetPath(input.projectRoot, targetFilePath);

    const sourceBuffer = await readFile(sourceFilePath);
    const rewrittenSourceBuffer = isBinaryBuffer(sourceBuffer)
      ? sourceBuffer
      : Buffer.from(
          rewriteComponentLocalImports(
            sourceBuffer.toString("utf-8"),
            fileTransfers,
          ),
          "utf-8",
        );
    let targetBuffer: Buffer | null = null;
    try {
      targetBuffer = await readFile(targetFilePath);
    } catch {
      targetBuffer = null;
    }

    if (!targetBuffer) {
      await ensureDir(path.dirname(targetFilePath));
      await writeFile(targetFilePath, rewrittenSourceBuffer);
      newFiles.push(targetRelativePath);
      continue;
    }

    if (rewrittenSourceBuffer.equals(targetBuffer)) {
      identicalFiles.push(targetRelativePath);
      continue;
    }

    const entry = await createConflictReportEntry({
      sourceFilePath,
      targetFilePath,
      exportPath,
      projectRoot: input.projectRoot,
      sourceBuffer: rewrittenSourceBuffer,
    });
    if (entry) {
      conflictReportEntries.push(entry);
      conflictFiles.push({
        targetPath: entry.targetPath,
        isBinary: entry.isBinary,
      });
    }
  }

  const { addedDependencies, dependenciesVersionConflicts } =
    await applyDependencyRequirementsToPackageJson({
      exportPackageJsonPath: path.join(exportPath, PACKAGE_JSON_RELATIVE_PATH),
      userPackageJsonPath: path.join(
        input.projectRoot,
        PACKAGE_JSON_RELATIVE_PATH,
      ),
      requiredPackageNames: dependencyGraph.externalPackages,
    });

  const missingManifestFileList = [...missingManifestFiles].sort((a, b) =>
    a.localeCompare(b),
  );
  const sortedConflictEntries = conflictReportEntries.sort((a, b) =>
    a.targetPath.localeCompare(b.targetPath),
  );
  const sortedConflictFiles = conflictFiles.sort((a, b) =>
    a.targetPath.localeCompare(b.targetPath),
  );
  const sortedNewFiles = newFiles.sort((a, b) => a.localeCompare(b));
  const sortedIdenticalFiles = identicalFiles.sort((a, b) =>
    a.localeCompare(b),
  );
  const copyId = createCopyId();
  const reportPath = path.join(
    input.exportsRoot,
    `${exportId}.copy-registry-component-report-${copyId}.json`,
  );
  const report: PrimeUiCopyRegistryComponentConflictReport = {
    reportPath,
    generatedAt: new Date().toISOString(),
    exportId,
    componentId: normalizedComponentId,
    componentImportPath,
    insertion,
    manifestFiles,
    copyableFiles,
    selectedBlock,
    skippedFiles,
    conflicts: sortedConflictEntries,
    missingManifestFiles: missingManifestFileList,
  };
  await writeFile(reportPath, `${JSON.stringify(report, null, 2)}\n`, "utf-8");

  const needsReview =
    copyableFiles.length === 0 ||
    sortedConflictFiles.length > 0 ||
    dependenciesVersionConflicts.length > 0 ||
    missingManifestFileList.length > 0;
  const status = needsReview ? "needs_review" : "completed";
  const guidanceArtifacts = [
    buildExportManifestArtifact(manifestPath),
    buildReferenceExportArtifact(exportPath),
  ];
  if (needsReview) {
    guidanceArtifacts.push(buildConflictReportArtifact(reportPath));
  }

  return {
    status,
    message: buildMessage({
      componentId: normalizedComponentId,
      componentImportPath,
      status,
      reportPath,
      newFiles: sortedNewFiles,
      identicalFiles: sortedIdenticalFiles,
      conflictFiles: sortedConflictFiles,
      skippedFiles,
      addedDependenciesCount: addedDependencies.length,
      dependencyConflictCount: dependenciesVersionConflicts.length,
      missingManifestFiles: missingManifestFileList,
    }),
    reportPath,
    exportId,
    exportPath,
    manifestPath,
    component: manifest.component,
    componentImportPath,
    insertion,
    manifestFiles,
    copyableFiles,
    selectedBlock,
    newFiles: sortedNewFiles,
    identicalFiles: sortedIdenticalFiles,
    conflictFiles: sortedConflictFiles,
    skippedFiles,
    addedDependencies,
    dependenciesVersionConflicts,
    missingManifestFiles: missingManifestFileList,
    summary: {
      totalManifestFiles: manifestFiles.length,
      totalCopyableFiles: copyableFiles.length,
      copiedFiles: sortedNewFiles.length,
      identicalFiles: sortedIdenticalFiles.length,
      conflictFiles: sortedConflictFiles.length,
      skippedFiles: skippedFiles.length,
      addedDependencies: addedDependencies.length,
      dependenciesVersionConflicts: dependenciesVersionConflicts.length,
      missingManifestFiles: missingManifestFileList.length,
    },
    guidance: {
      artifacts: guidanceArtifacts,
      requiredActions: `Do not install the skipped virtual component export page as a route. Manually import ${componentImportPath} into a real target page, install newly added dependencies, and resolve any reported conflicts or dependency version mismatches before treating the registry component transfer as complete.`,
    },
  };
}
